/**
 * Content for the Analytics MCP preview page. Analytics MCP is still in
 * development, which is what the three rules below are about.
 *
 * 1. Every sample question maps to a report that exists in the Analytics
 *    Library today, and names it. Grounding the examples in the real catalog is
 *    what keeps this page a preview of our product rather than a wishlist — if a
 *    question cannot be traced to a report, it does not belong here.
 * 2. No customer data. Every carrier, warehouse, dock, lane and number is
 *    invented. Carriers are lettered and warehouses are directional so nobody
 *    can read a real network into them.
 * 3. No promises about mechanics. The connector returns rows and the assistant
 *    reads them, so an answer is a sentence plus the rows behind it — never a
 *    computed metric we do not produce, and never a tool name or config shape
 *    that could still change.
 */

export const SHIPPER_GUIDE_PRODUCT = 'ShipperGuide – Freight'
export const OPENDOCK_PRODUCT = 'Opendock – Dock & Yard'
export const CROSS_PRODUCT = 'Both together'

// Section order for the question list. Anything not listed sorts after these,
// alphabetically.
const PRODUCT_ORDER = [SHIPPER_GUIDE_PRODUCT, OPENDOCK_PRODUCT, CROSS_PRODUCT]

// Short labels for the question rows — the long, qualified form is used for
// headings, and a row label has to stay on one line.
const TAB_LABEL_BY_PRODUCT: Record<string, string> = {
  [SHIPPER_GUIDE_PRODUCT]: 'ShipperGuide',
  [OPENDOCK_PRODUCT]: 'Opendock',
  [CROSS_PRODUCT]: 'Both',
}

/**
 * What the assistant read to answer. `reports` are real names from the
 * Analytics Library, which is the point: a visitor can go and look at the
 * report the answer came from.
 */
export type AnalyticsMcpLookup = {
  reports: Array<string>
  period: string
  rowsReturned: number
}

type AnalyticsMcpTable = {
  columns: Array<string>
  rows: Array<Array<string>>
}

type AnalyticsMcpAnswer = {
  // The assistant's reading of the rows, in one or two sentences. This is the
  // product: you get the sentence, and the rows are underneath it as evidence.
  summary: string
  table: AnalyticsMcpTable
  // The suggested next question — the moment a visitor sees that this is a
  // conversation and not a dashboard.
  followUp: string
}

export type AnalyticsMcpSample = {
  id: string
  product: string
  question: string
  lookup: AnalyticsMcpLookup
  answer: AnalyticsMcpAnswer
}

export const ANALYTICS_MCP_SAMPLES: Array<AnalyticsMcpSample> = [
  {
    id: 'tender-rejection-cost',
    product: SHIPPER_GUIDE_PRODUCT,
    question: 'What are tender rejections costing me, and which carriers?',
    lookup: { reports: ['Cost Leakage'], period: 'last quarter', rowsReturned: 1284 },
    answer: {
      summary:
        'Rejections cost you $244K against the rates you tendered. Carrier C is behind 43% of it, all on lanes where it had contracted volume.',
      table: {
        columns: ['Carrier', 'Rejected loads', 'Cost leakage', 'Share'],
        rows: [
          ['Carrier C', '148', '$104,200', '42.7%'],
          ['Carrier A', '96', '$61,800', '25.3%'],
          ['Carrier D', '74', '$48,300', '19.8%'],
          ['Carrier B', '52', '$29,700', '12.2%'],
        ],
      },
      followUp: 'Want to see which lanes Carrier C rejected, and who covered those loads instead?',
    },
  },
  {
    id: 'above-routing-guide',
    product: SHIPPER_GUIDE_PRODUCT,
    question: 'Where am I paying above my routing guide minimums?',
    lookup: { reports: ['Purchasing'], period: 'last quarter', rowsReturned: 214 },
    answer: {
      summary:
        'You paid above the routing guide minimum on 38% of loads. Five lanes account for most of the gap, and on three of them a cheaper in-network carrier was available.',
      table: {
        columns: ['Lane', 'Loads above minimum', 'Paid vs minimum', 'Potential savings'],
        rows: [
          ['Midwest → Southwest', '184', '+14.2%', '$86,400'],
          ['Southeast → Northeast', '126', '+11.8%', '$52,100'],
          ['West → Southwest', '98', '+9.4%', '$31,700'],
          ['South → Midwest', '74', '+7.1%', '$19,800'],
        ],
      },
      followUp: 'Want the in-network carriers that were available on those lanes?',
    },
  },
  {
    id: 'warehouse-dwell',
    product: OPENDOCK_PRODUCT,
    question: 'Which warehouses have the longest dwell times?',
    lookup: {
      reports: ['Opendock OTD & Logistics Times'],
      period: 'month to date',
      rowsReturned: 128,
    },
    answer: {
      summary:
        'Two warehouses sit more than an hour above the rest on dwell, and they are also your two weakest on on-time delivery.',
      table: {
        columns: ['Warehouse', 'Appointments', 'Average dwell', 'On-time delivery'],
        rows: [
          ['North DC', '612', '3h 28m', '74.1%'],
          ['South Cross-dock', '488', '3h 12m', '79.6%'],
          ['West RDC', '533', '2h 06m', '90.2%'],
          ['Central DC', '701', '1h 41m', '93.8%'],
        ],
      },
      followUp:
        'Want carrier waiting time split out, to see whether the delay starts before the door or after?',
    },
  },
  {
    id: 'dock-capacity',
    product: OPENDOCK_PRODUCT,
    question: 'Which docks are running out of slot capacity?',
    lookup: {
      reports: ['Opendock - Capacity Report'],
      period: 'the next 30 days',
      rowsReturned: 96,
    },
    answer: {
      summary:
        'Three docks are booked past 90% of their available slots, and all three are at the two warehouses with the longest dwell.',
      table: {
        columns: ['Dock', 'Capacity hours', 'Slots booked', 'Utilization'],
        rows: [
          ['North DC · Dock 3', '240', '231', '96.2%'],
          ['North DC · Dock 1', '240', '227', '94.8%'],
          ['South Cross-dock · Dock 2', '180', '165', '91.5%'],
          ['Central DC · Dock 4', '200', '137', '68.3%'],
        ],
      },
      followUp: 'Want to see which load types are filling those slots?',
    },
  },
  {
    id: 'late-dock-late-delivery',
    product: CROSS_PRODUCT,
    question: 'Which carriers are late at the dock and late on delivery?',
    lookup: {
      reports: ['Opendock Timeliness of Appointments', 'Carrier Scorecard'],
      period: 'the last 60 days',
      rowsReturned: 246,
    },
    answer: {
      summary:
        'Carrier D shows up in both: it misses the most appointment windows and by the widest margin, and it has your lowest on-time delivery. The others run late at the dock but still deliver on time.',
      table: {
        columns: ['Carrier', 'Late appointments', 'Average minutes late', 'On-time delivery'],
        rows: [
          ['Carrier D', '186', '74', '88.9%'],
          ['Carrier C', '142', '41', '91.4%'],
          ['Carrier A', '88', '22', '97.2%'],
          ['Carrier B', '61', '18', '95.6%'],
        ],
      },
      followUp:
        'Want this by warehouse, to see whether Carrier D is late everywhere or only at one site?',
    },
  },
  {
    id: 'dwell-to-charges',
    product: CROSS_PRODUCT,
    question: 'Where is dwell time turning into extra charges?',
    lookup: {
      reports: ['Accessorials Breakdown', 'Opendock OTD & Logistics Times'],
      period: 'last quarter',
      rowsReturned: 342,
    },
    answer: {
      summary:
        'Detention charges track dwell almost exactly. North DC has both the longest dwell and 61% of your detention spend.',
      table: {
        columns: ['Warehouse', 'Average dwell', 'Detention charges', 'Share of accessorials'],
        rows: [
          ['North DC', '3h 28m', '$148,400', '61.2%'],
          ['South Cross-dock', '3h 12m', '$52,800', '21.8%'],
          ['West RDC', '2h 06m', '$27,300', '11.3%'],
          ['Central DC', '1h 41m', '$13,600', '5.7%'],
        ],
      },
      followUp: 'Want the same view by carrier, to see who is driving the detention?',
    },
  },
]

/**
 * What a visitor can ask about, in their own vocabulary rather than the data
 * model's. Each one carries an example question, because an example does more
 * to explain the product than another sentence of description would.
 */
type AnalyticsMcpCapability = {
  title: string
  body: string
  example: string
}

export const ANALYTICS_MCP_CAPABILITIES: Array<AnalyticsMcpCapability> = [
  {
    title: 'Cost and rates',
    body: 'Spend and volume, cost per mile, accessorial hotspots, and where you paid above your routing guide.',
    example: 'Where am I paying above my minimums?',
  },
  {
    title: 'Carrier performance',
    body: 'Tender rejections, on-time pickup and delivery, invoice variances and tracking compliance.',
    example: 'Which carriers slipped last month?',
  },
  {
    title: 'Dock and yard',
    body: 'Appointments, dwell, check-in compliance, slot capacity and trailer assets by warehouse.',
    example: 'Which docks are running out of capacity?',
  },
  {
    title: 'Follow-up questions',
    body: 'Every answer narrows the next one. Keep going until you have what you need.',
    example: 'Now show me only Carrier D.',
  },
]

export function analyticsMcpProductLabel(product: string): string {
  return TAB_LABEL_BY_PRODUCT[product] ?? product
}

export function groupSamplePromptsByProduct(
  prompts: Array<AnalyticsMcpSample>
): Array<[string, Array<AnalyticsMcpSample>]> {
  const grouped = prompts.reduce(
    (acc, prompt) => {
      acc[prompt.product] = [...(acc[prompt.product] || []), prompt]
      return acc
    },
    {} as Record<string, Array<AnalyticsMcpSample>>
  )

  return Object.entries(grouped).sort(([left], [right]) => {
    const leftIndex = PRODUCT_ORDER.indexOf(left)
    const rightIndex = PRODUCT_ORDER.indexOf(right)

    if (leftIndex === -1 && rightIndex === -1) {
      return left.localeCompare(right)
    }
    if (leftIndex === -1) return 1
    if (rightIndex === -1) return -1
    return leftIndex - rightIndex
  })
}

/**
 * Names the reports the answer came from. Reading two of them is the whole
 * point of the cross-product questions, so both get named rather than summed
 * into something vague.
 */
export function formatLookup(lookup: AnalyticsMcpLookup): string {
  const reports =
    lookup.reports.length > 1
      ? `${lookup.reports.slice(0, -1).join(', ')} and ${lookup.reports[lookup.reports.length - 1]}`
      : lookup.reports[0]

  return `Reading ${reports} · ${lookup.period}`
}

export function formatRowsReturned(lookup: AnalyticsMcpLookup): string {
  const rows = lookup.rowsReturned === 1 ? '1 row' : `${lookup.rowsReturned.toLocaleString()} rows`
  return `${rows} returned`
}

/**
 * Numeric columns read better right-aligned, and in a result table everything
 * after the first column is a measure.
 */
export function isMeasureColumn(columnIndex: number): boolean {
  return columnIndex > 0
}
